import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, 
  ChevronRight, 
  Star, 
  Heart, 
  Truck, 
  Check,
  Loader2,
  ShoppingCart
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import { getProductById, getRelatedProducts } from '../services/productService';
import { useCart } from '../context/CartContext';
import { useFavorites } from '../context/FavoritesContext';

// Breadcrumb Component with slide-in animation
const Breadcrumbs = ({ path }) => {
  return (
    <motion.nav
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="flex items-center gap-2 text-sm text-muted-foreground mb-6"
    >
      {path.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && <ChevronRight className="w-4 h-4" />}
          {index === path.length - 1 ? (
            <span className="text-foreground font-medium">{item}</span>
          ) : (
            <Link 
              to={index === 0 ? '/' : '#'} 
              className="hover:text-foreground transition-colors"
            >
              {item}
            </Link>
          )}
        </React.Fragment>
      ))}
    </motion.nav>
  );
};

// Image Gallery Component
const ImageGallery = ({ images, productName }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;

    if (Math.abs(diff) > 50) {
      if (diff > 0 && selectedIndex < images.length - 1) {
        setSelectedIndex(selectedIndex + 1);
      } else if (diff < 0 && selectedIndex > 0) {
        setSelectedIndex(selectedIndex - 1);
      }
    }
    setTouchStart(null);
  };

  return (
    <div className="space-y-4">
      {/* Main Image */}
      <div 
        className="relative aspect-square bg-muted/30 rounded-[32px] overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <AnimatePresence mode="wait">
          <motion.img
            key={selectedIndex}
            src={images[selectedIndex]}
            alt={`${productName} - View ${selectedIndex + 1}`}
            className="w-full h-full object-cover"
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          />
        </AnimatePresence>

        {/* Mobile Navigation Arrows */}
        <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between px-4 md:hidden">
          <Button
            variant="ghost"
            size="icon"
            className="w-10 h-10 rounded-full bg-background/80 backdrop-blur-sm shadow-md"
            onClick={() => setSelectedIndex(Math.max(0, selectedIndex - 1))}
            disabled={selectedIndex === 0}
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="w-10 h-10 rounded-full bg-background/80 backdrop-blur-sm shadow-md"
            onClick={() => setSelectedIndex(Math.min(images.length - 1, selectedIndex + 1))}
            disabled={selectedIndex === images.length - 1}
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Mobile Dots Indicator */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 md:hidden">
          {images.map((_, index) => (
            <button
              key={index}
              className={`w-2 h-2 rounded-full transition-all ${
                index === selectedIndex 
                  ? 'w-6 bg-foreground' 
                  : 'bg-foreground/30'
              }`}
              onClick={() => setSelectedIndex(index)}
            />
          ))}
        </div>
      </div>

      {/* Thumbnail Strip - Desktop */}
      <div className="hidden md:flex items-center gap-3">
        {images.map((image, index) => (
          <motion.button
            key={index}
            onClick={() => setSelectedIndex(index)}
            className={`relative w-20 h-20 rounded-2xl overflow-hidden border-2 transition-all ${
              index === selectedIndex 
                ? 'border-foreground' 
                : 'border-transparent hover:border-muted-foreground/30'
            }`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <img
              src={image}
              alt={`${productName} thumbnail ${index + 1}`}
              className="w-full h-full object-cover"
            />
          </motion.button>
        ))}
        {images.length > 4 && (
          <div className="w-20 h-20 rounded-2xl bg-muted/50 flex items-center justify-center text-muted-foreground text-sm font-medium">
            +{images.length - 4} more
          </div>
        )}
      </div>
    </div>
  );
};

// Size Selector Component
const SizeSelector = ({ sizes, selectedSize, onSelectSize }) => {
  // Remove duplicates
  const uniqueSizes = [...new Set(sizes)];
  
  if (!sizes || sizes.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">Size</span>
        <span className="text-sm text-muted-foreground">EU Men</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {uniqueSizes.map((size) => (
          <motion.button
            key={size}
            onClick={() => onSelectSize(size)}
            className={`min-w-[52px] h-11 px-3 rounded-xl border-2 text-sm font-medium transition-all ${
              selectedSize === size
                ? 'border-foreground bg-foreground text-background'
                : 'border-border hover:border-foreground/50'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {size}
          </motion.button>
        ))}
      </div>
      <button className="text-sm text-muted-foreground underline hover:text-foreground transition-colors">
        Size guide
      </button>
    </div>
  );
};

// Color Selector Component
const ColorSelector = ({ colors, selectedColor, onSelectColor }) => {
  if (!colors || colors.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-foreground">Color</span>
        <span className="text-sm text-muted-foreground">· {selectedColor?.name || 'Select'}</span>
      </div>
      <div className="flex items-center gap-3">
        {colors.map((color) => (
          <motion.button
            key={color.name}
            onClick={() => onSelectColor(color)}
            className={`relative w-12 h-12 rounded-xl overflow-hidden border-2 transition-all ${
              selectedColor?.name === color.name
                ? 'border-foreground ring-2 ring-foreground ring-offset-2'
                : 'border-border hover:border-foreground/50'
            }`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{ backgroundColor: color.hex }}
          >
            {color.image && (
              <img
                src={color.image}
                alt={color.name}
                className="w-full h-full object-cover"
              />
            )}
          </motion.button>
        ))}
      </div>
    </div>
  );
};

// Add to Cart Button with Animation
const AddToCartButton = ({ onAddToCart, disabled }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleClick = async () => {
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1200));
    setIsLoading(false);
    setIsSuccess(true);
    onAddToCart();
    
    setTimeout(() => {
      setIsSuccess(false);
    }, 2000);
  };

  return (
    <motion.button
      onClick={handleClick}
      disabled={disabled || isLoading || isSuccess}
      className={`flex-1 h-14 rounded-full font-medium text-base transition-all flex items-center justify-center gap-2 ${
        isSuccess
          ? 'bg-success text-success-foreground'
          : 'bg-foreground text-background hover:bg-foreground/90'
      } disabled:opacity-50 disabled:cursor-not-allowed`}
      whileTap={{ scale: 0.98 }}
    >
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Loader2 className="w-5 h-5 animate-spin" />
          </motion.div>
        ) : isSuccess ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-2"
          >
            <Check className="w-5 h-5" />
            <span>Added!</span>
          </motion.div>
        ) : (
          <motion.span
            key="default"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            Add to cart
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  );
};

// Main Product Detail Page Component
const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart, cartItemsCount, isCartAnimating } = useCart();
  const { isFavorited, toggleFavorite, isHeartAnimating, favoritesCount } = useFavorites();
  
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      setIsLoading(true);
      try {
        const [productData, related] = await Promise.all([
          getProductById(productId),
          getRelatedProducts(productId, 4)
        ]);
        
        if (productData) {
          setProduct(productData);
          if (productData.colors && productData.colors.length > 0) {
            setSelectedColor(productData.colors[0]);
          }
          if (productData.availableSizes && productData.availableSizes.length > 0) {
            setSelectedSize(productData.availableSizes[0]);
          }
        }
        setRelatedProducts(related);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
    // Scroll to top when product changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [productId]);

  const handleAddToCart = () => {
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.images[0] || product.imageUrl,
      selectedSize,
      selectedColor,
      quantity: 1,
      brand: product.brand,
      rating: product.rating,
      reviews: product.reviews,
    });
    toast.success('Added to cart!', {
      description: `${product.name} has been added to your cart.`
    });
  };

  const handleBuyNow = () => {
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.images[0] || product.imageUrl,
      selectedSize,
      selectedColor,
      quantity: 1,
      brand: product.brand,
      rating: product.rating,
      reviews: product.reviews,
    });
    navigate('/cart');
  };

  const handleWishlist = () => {
    if (!product) return;
    toggleFavorite({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.images[0] || product.imageUrl,
      rating: product.rating,
      reviews: product.reviews,
      brand: product.brand,
      brandTag: product.brandTag,
      category: product.category,
      colors: product.colors,
      availableSizes: product.availableSizes,
    });
    const wasFavorited = isFavorited(product.id);
    toast.success(wasFavorited ? 'Removed from favorites' : 'Added to favorites');
  };

  const isProductFavorited = product ? isFavorited(product.id) : false;
  const isAnimating = product ? isHeartAnimating === product.id : false;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <h2 className="text-2xl font-display font-semibold">Product not found</h2>
        <Button onClick={() => navigate('/')} variant="outline" className="rounded-full">
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Shop
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header with Back Button */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <motion.div 
              className="flex items-center gap-2 cursor-pointer"
              whileHover={{ scale: 1.02 }}
              onClick={() => navigate('/')}
            >
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-primary-foreground font-display font-bold text-sm">S</span>
              </div>
              <span className="font-display font-bold text-xl text-foreground">StuffLab</span>
            </motion.div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors font-medium">
                Women
              </Link>
              <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors font-medium">
                Men
              </Link>
              <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors font-medium">
                Kids
              </Link>
              <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors font-medium">
                Sports
              </Link>
              <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors font-medium">
                Bands
              </Link>
              <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors font-medium">
                New
              </Link>
              <Link to="/" className="text-destructive hover:text-destructive/80 transition-colors font-medium">
                Sale
              </Link>
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search"
                    className="h-10 pl-4 pr-10 rounded-full bg-muted/50 border-0 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>
              <motion.div
                animate={isCartAnimating ? { scale: [1, 1.3, 1] } : {}}
                transition={{ duration: 0.4 }}
              >
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="relative rounded-full"
                  onClick={() => navigate('/cart')}
                >
                  <ShoppingCart className="w-5 h-5" />
                  {cartItemsCount > 0 && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      key={cartItemsCount}
                    >
                      <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-warning text-foreground text-xs rounded-full">
                        {cartItemsCount > 99 ? '99+' : cartItemsCount}
                      </Badge>
                    </motion.div>
                  )}
                </Button>
              </motion.div>
              <Button 
                variant="ghost" 
                size="icon" 
                className="relative rounded-full"
                onClick={() => navigate('/favorites')}
              >
                <Heart className={`w-5 h-5 ${favoritesCount > 0 ? 'fill-destructive text-destructive' : ''}`} />
                {favoritesCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-destructive text-destructive-foreground text-xs rounded-full">
                    {favoritesCount}
                  </Badge>
                )}
              </Button>
              <div className="w-8 h-8 rounded-full bg-muted overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100"
                  alt="User"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
        {/* Breadcrumbs */}
        <Breadcrumbs path={product.categoryPath || ['Home', product.category, product.brand]} />

        {/* Product Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <ImageGallery images={product.images} productName={product.name} />
          </motion.div>

          {/* Product Info - Sticky on Desktop */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
            className="lg:sticky lg:top-28 lg:self-start space-y-6"
          >
            {/* Brand */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-foreground rounded-lg flex items-center justify-center">
                <span className="text-background font-display font-bold text-sm">
                  {product.brandLogo || product.brand?.charAt(0)}
                </span>
              </div>
              <span className="font-medium text-foreground">{product.brand}</span>
              <span className="ml-auto text-sm text-muted-foreground">{product.sku}</span>
            </div>

            {/* Product Title */}
            <h1 className="font-display font-bold text-3xl md:text-4xl text-foreground leading-tight">
              {product.name}
            </h1>

            {/* Rating */}
            <div className="flex items-center gap-2">
              <div className="flex items-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-5 h-5 ${
                      star <= Math.floor(product.rating)
                        ? 'fill-warning text-warning'
                        : star - 0.5 <= product.rating
                        ? 'fill-warning/50 text-warning'
                        : 'fill-muted text-muted'
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-muted-foreground">
                {product.reviews} reviews
              </span>
            </div>

            {/* Price */}
            <div className="text-3xl font-bold text-foreground">
              ${product.price.toFixed(2)}
            </div>

            <Separator />

            {/* Color Selection */}
            <ColorSelector
              colors={product.colors}
              selectedColor={selectedColor}
              onSelectColor={setSelectedColor}
            />

            {/* Size Selection */}
            <SizeSelector
              sizes={product.availableSizes}
              selectedSize={selectedSize}
              onSelectSize={setSelectedSize}
            />

            <Separator />

            {/* Add to Cart & Wishlist */}
            <div className="flex items-center gap-3">
              <AddToCartButton
                onAddToCart={handleAddToCart}
                disabled={product.availableSizes?.length > 0 && !selectedSize}
              />
              <motion.button
                onClick={handleWishlist}
                className={`w-14 h-14 rounded-full border-2 flex items-center justify-center transition-all ${
                  isProductFavorited
                    ? 'border-destructive bg-destructive/10 text-destructive'
                    : 'border-border hover:border-foreground/50'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                animate={isAnimating ? { scale: [1, 1.3, 1] } : {}}
              >
                <Heart className={`w-5 h-5 ${isProductFavorited ? 'fill-current' : ''}`} />
              </motion.button>
            </div>

            {/* Free Delivery Notice */}
            {product.freeDelivery && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex items-center gap-2 text-sm text-muted-foreground"
              >
                <Truck className="w-4 h-4" />
                <span>Free delivery on orders over ${product.freeDeliveryMin?.toFixed(1) || '30.0'}</span>
              </motion.div>
            )}

            {/* Description */}
            {product.description && (
              <div className="pt-4">
                <h3 className="font-semibold text-foreground mb-2">Description</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {product.description}
                </p>
              </div>
            )}

            {/* Features */}
            {product.features && product.features.length > 0 && (
              <div>
                <h3 className="font-semibold text-foreground mb-2">Features</h3>
                <ul className="space-y-2">
                  {product.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-muted-foreground">
                      <Check className="w-4 h-4 text-success" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="mt-16 md:mt-24">
            <h2 className="font-display font-semibold text-2xl text-foreground mb-8">
              You may also like
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {relatedProducts.map((relatedProduct, index) => (
                <motion.div
                  key={relatedProduct.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="group cursor-pointer"
                  onClick={() => navigate(`/product/${relatedProduct.id}`)}
                >
                  <div className="aspect-square bg-muted/30 rounded-2xl overflow-hidden mb-3">
                    <img
                      src={relatedProduct.imageUrl}
                      alt={relatedProduct.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <h3 className="font-medium text-foreground text-sm mb-1 line-clamp-1">
                    {relatedProduct.name}
                  </h3>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-foreground">
                      ${relatedProduct.price.toFixed(2)}
                    </span>
                    <div className="flex items-center gap-1">
                      <Star className="w-3 h-3 fill-warning text-warning" />
                      <span className="text-xs text-muted-foreground">{relatedProduct.rating}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default ProductDetailPage;
